import { cn } from '@reelplicate/ui';
import type { CSSProperties } from 'react';

import { type PostStatus, STATUS_ACCENTS, STATUS_LABELS } from './calendar-model';
import { type PlatformMark, PlatformStack } from './platform-stack';

export type PostChipProps = {
  title: string;
  /** "09:00 · O&E" */
  meta: string;
  status: PostStatus;
  /** A poster frame; a plain tile until one exists. */
  thumbnail: string | null;
  /** Where it goes; each dot badged with where it stands there. */
  platforms: PlatformMark[];
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
};

/**
 * Figma "Calendar / Post chip" (142:927): a status accent bar, a 9:16
 * thumbnail, time · account, the title and the platforms it goes to.
 * Stacked platform marks = cross-post. The accent is the worst state across
 * its platforms (failed, then not found yet, then scheduled, then published).
 */
export function PostChip({
  title,
  meta,
  status,
  thumbnail,
  platforms,
  className,
  style,
  onClick,
}: PostChipProps) {
  return (
    <button
      type="button"
      aria-label={`${title}, ${meta}, ${STATUS_LABELS[status]}`}
      onClick={onClick}
      style={style}
      className={cn(
        'bg-bg-surface border-border-subtle focus-visible:shadow-focus-ring hover:border-border-default flex items-start gap-2 overflow-hidden rounded-md border py-1.5 pr-2 pl-1.5 text-left outline-none',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'w-[3px] shrink-0 self-stretch rounded-[2px]',
          STATUS_ACCENTS[status],
        )}
      />
      {thumbnail ? (
        <img
          src={thumbnail}
          alt=""
          className="h-[42px] w-6 shrink-0 rounded-xs object-cover"
        />
      ) : (
        <span aria-hidden className="bg-bg-subtle h-[42px] w-6 shrink-0 rounded-xs" />
      )}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-caption text-text-secondary truncate">{meta}</span>
        <span className="text-label-sm text-text-primary line-clamp-2">{title}</span>
        <PlatformStack platforms={platforms} />
      </span>
    </button>
  );
}
